import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import SectionCaseStudiesItem from '../sections/SectionCaseStudiesItem';

const CASE_STUDIES_URL = `${process.env.PUBLIC_URL || ''}/case-studies/index.json`;

/**
 * Case Studies landing page (#casestudies) – dedicated page with intro + full list.
 */
export default function CaseStudiesPage({ onBack, onContact }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(CASE_STUDIES_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = Array.isArray(data?.items) ? data.items : [];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--jb-forest-bg)' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 0%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 90% 80%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8 pt-24 pb-20">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-10 transition text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <header className="mb-14 max-w-3xl">
          <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-3">
            {data?.eyebrow ?? 'Insights'}
          </p>
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-5 leading-tight tracking-tight">
            {data?.title ?? data?.heading ?? 'Case studies'}
          </h1>
          {(data?.intro || data?.subtext) && (
            <p className="font-body text-lg text-text-body leading-relaxed mb-8">
              {data?.intro ?? data?.subtext}
            </p>
          )}
          <button
            type="button"
            onClick={onContact}
            className="jb-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
          >
            {data?.ctaText ?? 'Talk to us'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </header>

        <section>
          <div className="mb-8">
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-2">Library</p>
            <h2 className="font-display font-extrabold text-2xl text-text-hero">All case studies</h2>
          </div>

          {loading ? (
            <p className="font-body text-text-sub">Loading…</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item) => (
                <SectionCaseStudiesItem
                  key={item.slug || item.title}
                  title={item.title ?? ''}
                  description={item.shortDescription ?? item.description ?? ''}
                  href={item.slug ? `#casestudy/${item.slug}` : (item.href ?? '#')}
                  date={item.date ?? ''}
                  internal={!!item.slug}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
